import axios from "axios";
import constants from "./constants";
import { baseURL } from "../core";
import { readToken, clearSession } from "./session";
import { isPublicPath } from "./publicPaths";

// AUTH-04. One place that reacts to "your session is gone".
//
// WHAT WAS WRONG
// There are four axios interceptors in the redux services (User, agent, agency) and every
// one of them is a pass-through with its body commented out. So a 401 from VT-Backend did
// nothing at all: the page kept rendering, each list came back empty, and the user was
// left looking at what seemed to be a broken app rather than being asked to sign in.
// src/Util/hubSession.js has a real 401 handler, but it fires only for requests carrying a
// hub session — VT-Backend calls never match, by design.
//
// WHAT THIS DOES
// On a 401 from VT-Backend it clears the session and sends the browser to /login, keeping
// where the user was so they can be put back. Hub responses are left to hubSession.js, and
// third-party hosts (Google, Flywire, S3) are ignored entirely.
//
// EVERY CLIENT (October 2026)
// At first this sat on the default axios and on api/requestMethods.js only. A client made
// by axios.create does not inherit it, and 25 backend clients in 20 files are made that
// way — the app's own "who am I" call on page load among them (pages/index.jsx). With a
// sign-in the backend refused, 15 of the 26 pages that need one stayed up under the agent's
// name with empty lists. It is now put on every client as it is built (bottom of this
// file), which is why a call is judged by the full address it goes to, not by its path.

const ABSOLUTE = /^([a-z][a-z\d+\-.]*:)?\/\//i;

// Where a call really goes: the client's base address joined with the path, as axios does
// it. `config.url` alone is only the path on a client that has a base address — "/x" on the
// Guesty client is Guesty's, and "travel-agency/x" on a backend client is ours.
export const targetOf = (config) => {
  const url = (config && config.url) || "";
  const base = (config && config.baseURL) || "";
  if (!base || ABSOLUTE.test(url)) return url;
  return url ? `${base.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}` : base;
};

// constants.BASE_URL is fixed; core's baseURL follows REACT_APP_API_BASE_URL (a local
// backend). Both are VT-Backend.
const BACKENDS = [constants.BASE_URL, baseURL].filter(Boolean);

const isBackendUrl = (target = "") => {
  if (!target) return false;
  if (target.startsWith("/")) return true; // a path with no base address: the default axios
  return BACKENDS.some((backend) => target.startsWith(backend));
};

const isHubUrl = (url = "") =>
  Boolean(url) && (url.startsWith(constants.SHUB_URL) || (constants.SHUB_SOURCE_URL && url.startsWith(constants.SHUB_SOURCE_URL)));

// /hub-booking/* hands the hub's own answer back unchanged (VT-Backend
// controllers/hubBooking.js). A 401 carrying the hub's refusal ({ ok: false, code }) is the
// hub saying no to the booking: the agent's sign-in was accepted, or the request would never
// have reached the hub. Ending the session there signed an admin out in the middle of an
// approval and hid the hub's message. The sign-in check's own 401 on the same route
// ({ message }) still ends the session.
const isRelayedHubAnswer = (target, data) =>
  /\/hub-booking\//.test(target) && Boolean(data) && typeof data === "object" && (data.ok === false || Boolean(data.code));

// Pages a visitor may open that are also part of the signed-in app: they carry its menu and
// its actions (wish list, favourite).
const APP_PAGES_OPEN_TO_VISITORS = ["/property", "/hotdestinations", "/tutorial-videos", "/faq", "/intouch"];
const isAppPage = (pathname = "") => APP_PAGES_OPEN_TO_VISITORS.some((page) => pathname === page || pathname.startsWith(`${page}/`));

// A page that needs a sign-in always asks for one. A public page has no session to lose —
// unless the browser holds a sign-in the backend has just refused: that is an agent, not a
// visitor, and on the app's own pages they are asked to sign in too. The other public pages
// never redirect: /login itself answers 401 for a bad password (a loop), and the links sent
// by email or shared with clients, and the data room, are opened by people who never sign in.
const shouldRedirect = (pathname, hadSignIn) => !isPublicPath(pathname) || (hadSignIn && isAppPage(pathname));

export const handleUnauthorized = (error, { location = window.location, redirect } = {}) => {
  const status = error && error.response && error.response.status;
  if (status !== 401) return false;
  const target = targetOf(error.config);
  if (!isBackendUrl(target) || isHubUrl(target)) return false;
  if (isRelayedHubAnswer(target, error.response.data)) return false;
  if (!shouldRedirect(location.pathname, Boolean(readToken()))) return false;

  clearSession();
  const next = encodeURIComponent(`${location.pathname}${location.search || ""}`);
  const destination = `/login?next=${next}&expired=1`;
  if (typeof redirect === "function") redirect(destination);
  else location.href = destination;
  return true;
};

// The other half of ?next=: where the sign-in page sends the agent once they are back in
// (pages/Auth/Auth.jsx). It was written above and read by nobody — the reader sat in a
// callback the sign-in had stopped using, so everyone landed on /search. Only a path on this
// site is honoured: anything a browser could read as another host ("//host", "/\host", a
// path with a tab or newline in it) would be an open redirect.
const SAME_SITE_PATH = /^\/(?!\/)[^\\\u0000-\u001f]*$/;
export const returnPath = (search = "") => {
  const next = new URLSearchParams(search).get("next");
  return next && SAME_SITE_PATH.test(next) ? next : null;
};

// The other half of &expired=1: the sign-in page tells the agent why they are there
// (pages/Auth/Auth.jsx). Until 2026-10-27 nothing read it, so an agent whose session ran out
// landed on a bare sign-in form with no reason given.
export const EXPIRED_NOTICE = "Your session has expired. Please log in again and you'll be taken back to the page you were on.";
export const expiredNotice = (search = "") => (new URLSearchParams(search || "").get("expired") === "1" ? EXPIRED_NOTICE : null);

// Once per client: api/requestMethods.js and pages/Clients/clientsRequest.js still ask for
// it by name, and a second handler on the same client would end the session twice.
const installed = new WeakSet();

export const installAuthInterceptor = (instance = axios) => {
  if (installed.has(instance)) return instance;
  installed.add(instance);
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      handleUnauthorized(error);
      return Promise.reject(error);
    }
  );
  return instance;
};

// Keeps a long-lived instance's Authorization header in step with the current token.
// src/api/requestMethods.js used to bake `localStorage.getItem("jToken")` into its headers
// at MODULE LOAD, so after signing in during the same tab those instances still carried the
// old value — or the literal string "Bearer null".
export const installTokenRefresh = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = readToken();
    config.headers = config.headers || {};
    if (token) config.headers.Authorization = `Bearer ${token}`;
    else delete config.headers.Authorization;
    return config;
  });
  return instance;
};

// Every client built from here on gets the handler. src/index.js imports this file right
// after hubSession.js (which wraps axios.create the same way, for hub sessions) and before
// any page, so no client is built without it. Hub and third-party clients carry it too and
// are untouched by it: handleUnauthorized acts only on an answer from VT-Backend's address.
let everyClient = false;
export const installOnEveryClient = () => {
  if (everyClient) return;
  everyClient = true;
  const create = axios.create.bind(axios);
  axios.create = (config) => installAuthInterceptor(create(config));
};

installAuthInterceptor(axios);
installOnEveryClient();

export default installAuthInterceptor;
